import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useSearchParams } from 'react-router-dom';
import "bootstrap/dist/css/bootstrap.min.css";
import { Row, Col } from "react-bootstrap";
import "../../style/EnterParticipantName.css";
import TriviaMakerLogo from "../../image/TriviaMaker-Logo.png";
import { Filter } from 'bad-words';
import axios from "axios";
import NoSleep from "nosleep.js";  // Import NoSleep.js
import firebase from '../../firebase';

export default function EnterParticipantName({ onNavigate, code: propCode }) {
  const { code: urlCode } = useParams();
  const code = propCode || urlCode;
  const badWordsFilter = new Filter();
  const [backgroundImg, setbackgroundImg] = useState(null);
  const [backgroundColor, setbackgroundColor] = useState(null);
  const [gameName, setGameName] = useState(null);
  const [logo, setLogo] = useState(null);
  const [bgLoading, setBgLoading] = useState(true);
  const [logoImgLoading, setLogoImgLoading] = useState(true);
  const [teamNames, setTeamNames] = useState(null);
  const [profileColor, setProfileColor] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false); // New state variable to track submission status
  const [isNameTaken, setIsNameTaken] = useState(false);
  const [imgLoading, setImgLoading] = useState(true);
  const noSleep = new NoSleep();
  const backgroundStyle = backgroundImg
    ? {
      backgroundImage: `url(${backgroundImg})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    }
    : { backgroundColor };

  // Regular expression to allow only letters (A-Z, a-z)
  const nameRegex = /^[A-Za-z]+$/;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `https://trivia-maker-tv.firebaseio.com/AppleTV/${code}.json`
        );
        const data = await response.data;
        setGameName(data.gameName);
        setTeamNames(data.teamNames);
        setbackgroundColor(data.backgroundColor);

        // Preload game logo
        if (data.logo) {
          const logoImg = new Image();
          logoImg.src = data.logo;
          logoImg.onload = () => {
            setLogo(data.logo);
            setLogoImgLoading(false);
          };
          logoImg.onerror = () => {
            setLogoImgLoading(false);
          };
        } else {
          setLogo(null);
          setLogoImgLoading(false);
        }

        // Preload background image (or use default)
        const bgUrl = data.backgroundImg || "https://triviamaker.com/TriviaMaker/Theme/v4/HangMan/Cowboy/cowboyBG.jpg";
        setbackgroundImg(bgUrl);
        const bgImg = new Image();
        bgImg.src = bgUrl;
        bgImg.onload = () => setBgLoading(false);
        bgImg.onerror = () => setBgLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        // Redirect to the play URL
        window.location.href = "https://play.triviamaker.com/";
      }
    };
    fetchData();
  }, [code]);

  useEffect(() => {
    if (code) {
      const db = firebase.database();
      const ref = db.ref(`AppleTV/${code}`);
      let previousData;
      ref.on("value", (snapshot) => {
        const data = snapshot.val();
        if (data !== previousData) {
          // console.log("Data updated:", data);
          previousData = data;

          const teamParticipants = data !== null && 'teamNames' in data ? data.teamNames.map((team, index) => {
            return {
              initials: team.initials,
              name: team.name,
              profileColor: team.profileColor,
            };
          }) : [];

        }
      });
    }
  }, [code]);

  // window.exitgame is set by parent App.js component - no need to set it here

  const validateInput = () => {
    let isValid = true;

    // Regex to allow alphabets and numbers, but no special characters
    const nameRegex = /^[a-zA-Z0-9]+$/;

    // Validate first & last name
    if (!firstName || !lastName) {
      setFirstNameError("Please Enter First name and Last name");
      isValid = false;
    } else if (!nameRegex.test(firstName) || !nameRegex.test(lastName)) {
      setFirstNameError("Special characters are not allowed.");
      isValid = false;
    } else if (badWordsFilter.isProfane(firstName) || badWordsFilter.isProfane(lastName)) {
      setFirstNameError("Please enter a valid name. No profanity allowed.");
      isValid = false;
    } else {
      setFirstNameError("");
    }

    return isValid;
  };

  const hashCode = (name) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash); // simple hashing formula
    }
    return hash;
  };

  const intToRGB = (hash) => {
    const color = (hash & 0x00ffffff).toString(16).toUpperCase();
    return "00000".substring(0, 6 - color.length) + color;
  };

  const generateProfileColor = (name) => {
    const colorHash = intToRGB(hashCode(name)); // generate color
    const color = `#${colorHash}`;
    setProfileColor(color); // Store the generated color
  };


  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      noSleep.enable();

    }
    catch (error) {
      alert(`Unable to disable sleep: ${error}`);
    }
    if (validateInput()) {
      const name = `${firstName} ${lastName}`;
      noSleep.enable();
      const initials = `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
      const generatedProfileColor = `#${intToRGB(hashCode(name))}`;

      const db = firebase.database();
      const ref = db.ref(`AppleTV/${code}`);

      try {
        const snapshot = await ref.once('value');
        const currentData = snapshot.val() || {};
        const teamNamesArr = Array.isArray(currentData.teamNames) ? currentData.teamNames : [];
        const participantLimit = currentData.participantlimit || 0;

        if (participantLimit > 0 && teamNamesArr.length >= participantLimit) {
          alert("Maximum number of participants reached for this game.");
          window.location.href = "https://play.triviamaker.com/";
          return;
        }

        // Use a transaction to ensure safe concurrent updates
        const result = await ref.transaction((currentData) => {
          if (!currentData) {
            currentData = {
              teamNames: [],
              teams: [],
              team1bgColor: '',
              team2bgColor: '',
            };
          }

          currentData.teamNames = Array.isArray(currentData.teamNames) ? currentData.teamNames : [];
          currentData.teams = Array.isArray(currentData.teams) ? currentData.teams : [];

          // Check if the name already exists in the `teamNames` array
          const nameExists = currentData.teamNames.some(
            (team) => team.name.toLowerCase() === name.toLowerCase()
          );

          if (nameExists) {
            return; // Abort transaction
          }

          const newTeamMember = {
            initials,
            name,
            profileColor: generatedProfileColor,
          };

          currentData.teamNames.push(newTeamMember);

          const teamIndex = currentData.teamNames.length - 1;
          const teams = currentData.teams.length > 0 ? currentData.teams : ["Team 1", "Team 2"];
          const allocatedTeam = teams[teamIndex % teams.length];
          newTeamMember.allocatedTeam = allocatedTeam;

          localStorage.setItem('initials', initials);
          localStorage.setItem('name', name);
          localStorage.setItem('profileColor', generatedProfileColor);
          localStorage.setItem('allocatedTeam', allocatedTeam);

          const team1bgColor = currentData.team1bgColor || '';
          const team2bgColor = currentData.team2bgColor || '';

          if (allocatedTeam === teams[0] && team1bgColor) {
            localStorage.setItem('allocatedTeamColor', team1bgColor);
          } else if (allocatedTeam === teams[1] && team2bgColor) {
            localStorage.setItem('allocatedTeamColor', team2bgColor);
          }

          return currentData;
        });

        if (!result.committed) {
          // Transaction aborted because name exists
          alert("Participant name is already taken by another user. Please choose a different name.");
          setFirstName("");
          setLastName("");
          setIsNameTaken(true);
          setIsSubmitting(false);
          return;
        }

        console.log("Form submitted successfully with updated team names");
        onNavigate('AllocatedTeamToParticipant');
      } catch (error) {
        console.error("Error submitting form", error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      console.log("Form submission blocked due to validation errors");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mainEnterParticipantName">
      <Row>
        <Col className="h-100 w-100 d-flex justify-content-center align-center EnterParticipantRow">
          <div className="nameenterscreendiv">
            <div className="divLogo">

              <img
                src={TriviaMakerLogo}
                alt="triviaLogo"
                className="triviaLogo"

              />
              <br />
            </div>
            <div className="namediv">
              <div className="card">
                <div style={backgroundStyle} className="backgroundDiv">
                  {(imgLoading || bgLoading || logoImgLoading) && (
                    <div
                      className="image-loading-spinner"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        minHeight: '120px', // Ensures spinner is vertically centered even if image is small
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 2,
                        background: 'transparent'
                      }}
                    >
                      <div
                        className="spinner-border"
                        role="status"
                        style={{ width: '2.5rem', height: '2.5rem', color: 'black' }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                  <img src={logo} alt="gamelogo" className="card-img-top"
                    style={{ display: imgLoading ? 'none' : 'block' }}
                    onLoad={() => setImgLoading(false)} />
                </div>
                <div className="card-body gamenametextclass">
                  <p className="card-text">{gameName}</p>
                </div>
              </div>

              <div className="secondDiv fw-bold">
                Enter your name to join the game.
              </div>

              <div className="colordiv">
                <div>
                  <span
                    style={{
                      color: "red",
                      width: "100%",
                      display: firstNameError ? "block" : "none",
                    }}
                    id="firstnameerrorid"
                  >
                    {firstNameError}
                  </span>
                </div>
                <div>
                  <span
                    style={{
                      color: "red",
                      width: "100%",
                      display: lastNameError ? "block" : "none",
                    }}
                    id="lastnameerrorid"
                  >
                    {lastNameError}
                  </span>
                </div>
                <div className="d-flex">
                  <input
                    id="participant-first-name"
                    type="text"
                    placeholder="First name"
                    className="form-control participant-first-name"
                    value={firstName}
                    maxLength={20}
                    onChange={(e) => setFirstName(e.target.value)}
                  />


                  <input
                    id="participant-last-name"
                    type="text"
                    placeholder="Last name"
                    className="form-control participant-last-name "
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    maxLength={20}
                  />
                </div>
              </div>

              <div className="btndiv">
                <button className="nextbtn" disabled={isSubmitting} onClick={handleSubmit}>
                  Join Game
                </button>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}
